import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MOVEMENT_KIND_LABELS, MOVEMENT_KINDS, formatQuantityMilli, type Paginated, type StockMovement } from '@kontor/shared';
import { api, queryString } from '../../api';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { SortTh } from '../../components/SortTh';
import { useSession } from '../../session';
import { InventoryTabs, dateTimeFormat, errorText } from './shared';
import './inventory.css';

const signed = (m: StockMovement) => `${m.quantityMilli > 0 ? '+' : ''}${formatQuantityMilli(m.quantityMilli)} ${m.unitSymbol ?? ''}`;

/** Tabelle der Bewegungen, auch für die Artikelansicht. Stornieren geht für Buchungen, die nicht selbst Storno oder schon storniert sind. */
export function MovementTable({ movements, loading, compact, sort, onSort, onReverse }: { movements: StockMovement[]; loading?: boolean; compact?: boolean; sort?: string; onSort?: (s: string) => void; onReverse?: (m: StockMovement) => void }) {
  return (
    <div className={compact ? '' : 'card table-card'}>
      <table className="table table--cards">
        <thead>
          <tr>
            {onSort ? <SortTh label="Zeit" sortKey="createdAt" sort={sort ?? ''} onSort={onSort} first="desc" /> : <th>Zeit</th>}
            <th>Art</th>
            {onSort ? <SortTh label="Artikel" sortKey="product" sort={sort ?? ''} onSort={onSort} /> : <th>Artikel</th>}
            <th>Lagerort</th>
            {onSort ? <SortTh label="Menge" sortKey="quantity" sort={sort ?? ''} onSort={onSort} className="num" /> : <th className="num">Menge</th>}
            <th>Hinweis</th>
            {onReverse && <th />}
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={7} className="muted">Wird geladen …</td></tr>}
          {!loading && movements.length === 0 && <tr><td colSpan={7} className="empty">Keine Bewegungen.</td></tr>}
          {movements.map((m) => (
            <ExpandRow
              key={m.id}
              className={m.reversed ? 'inv-reversed' : ''}
              summary={<Summary title={<Link to={`/lager/bestand/${m.productId}`} className="strong-link">{m.productName}</Link>} aside={<strong className={m.quantityMilli < 0 ? 'inv-bad' : 'inv-ok'}>{signed(m)}</strong>} line2={`${MOVEMENT_KIND_LABELS[m.kind]} · ${m.locationPath}`} line3={dateTimeFormat.format(new Date(m.createdAt))} />}
            >
              <td className="cell-desktop-title">{dateTimeFormat.format(new Date(m.createdAt))}{m.createdByName && <div className="muted small">{m.createdByName}</div>}</td>
              <td data-label="Art">{MOVEMENT_KIND_LABELS[m.kind]}{m.reversed && <div className="muted small">storniert</div>}</td>
              <td data-label="Artikel"><Link to={`/lager/bestand/${m.productId}`}>{m.productName}</Link>{m.batchNumber && <div className="muted small">Charge {m.batchNumber}</div>}</td>
              <td data-label="Lagerort">{m.locationPath}</td>
              <td data-label="Menge" className={`num ${m.quantityMilli < 0 ? 'inv-bad' : 'inv-ok'}`}>{signed(m)}</td>
              <td data-label="Hinweis">{[m.orderNumber, m.referenceText, m.note].filter(Boolean).join(' · ') || '–'}</td>
              {onReverse && (
                <td className="doc-actions">
                  {m.kind !== 'reversal' && !m.reversed && <button type="button" className="btn btn--secondary btn--sm" onClick={() => onReverse(m)}>Stornieren</button>}
                </td>
              )}
            </ExpandRow>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Alle Bewegungen des Lagers (KON-166): unveränderlich, ein Fehler wird per Storno als Gegenbuchung behoben */
export function MovementsPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const kind = params.get('kind') ?? '';
  const productId = params.get('productId') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const sort = params.get('sort') ?? '';
  const page = Number(params.get('page') ?? 1);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const list = useQuery({
    queryKey: ['inventory-movements', { q, kind, productId, from, to, sort, page }],
    queryFn: () => api<Paginated<StockMovement>>('GET', `/inventory/movements${queryString({ q, kind, productId, from, to, sort, page, perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };
  const reverse = useMutation({
    mutationFn: ({ m, reason }: { m: StockMovement; reason: string }) => api('POST', `/inventory/movements/${m.id}/reverse`, { reason }),
    onSuccess: () => {
      for (const key of ['inventory-movements', 'inventory-stock', 'inventory-detail', 'inventory-overview']) client.invalidateQueries({ queryKey: [key] });
      setMessage({ ok: true, text: 'Die Buchung ist storniert.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Stornieren hat nicht geklappt.') }),
  });
  const fields: FilterField[] = [
    { key: 'kind', label: 'Art', type: 'select', options: [{ value: '', label: 'Alle' }, ...MOVEMENT_KINDS.map((k) => ({ value: k, label: MOVEMENT_KIND_LABELS[k] }))] },
    { key: 'from', label: 'Von', type: 'date' },
    { key: 'to', label: 'Bis', type: 'date' },
  ];
  return (
    <>
      <div className="page-head">
        <h1>Bewegungen</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} insgesamt` : ''}</span>
      </div>
      <InventoryTabs />
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      <FilterBar search={{ value: q, placeholder: 'Artikel, Charge, Auftrag oder Notiz', onSubmit: (v) => update({ q: v }) }} fields={fields} values={{ kind, from, to }} onChange={(k, v) => update({ [k]: v })} onReset={() => setParams(new URLSearchParams())} />
      {productId && <p className="muted small">Gefiltert nach einem Artikel. <button type="button" className="link-button" onClick={() => update({ productId: '' })}>Filter aufheben</button></p>}
      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}
      <MovementTable
        movements={list.data?.data ?? []}
        loading={list.isLoading}
        sort={sort}
        onSort={(s) => update({ sort: s })}
        onReverse={can('inventory.write') ? (m) => {
          const reason = window.prompt(`Buchung „${MOVEMENT_KIND_LABELS[m.kind]} ${m.productName}“ stornieren. Grund:`);
          if (reason && reason.trim()) reverse.mutate({ m, reason: reason.trim() });
        } : undefined}
      />
      {list.data && list.data.meta.lastPage > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </div>
      )}
    </>
  );
}
